import { TenantEmailDiscoveryResponse } from 'api';
import { SIGN_IN_PRODUCT, SIGN_IN_VIEW } from 'common/constants/auth';
import { LoadingState } from 'components/ui';
import {
  OTP,
  WorkspaceSelector,
} from 'pages/Auth/components';
import { isEnterpriseSsoSignInSession } from 'pages/Auth/utils';
import SignInIdentifierStep from '../SignInIdentifierStep';
import SignInPasswordStep from '../SignInPasswordStep';
import SignInProductSelectionStep from '../SignInProductSelectionStep';
import SignInTermsStep from '../SignInTermsStep';
import type { SignInStepContentProps } from './SignInStepContent.types';
import { shouldShowSignInSessionStepLoading } from '../../SignIn.utils';

const SignInStepContent = ({
  currentLoadingLabel,
  currentUser,
  handleGoogleLogin,
  handleIdentifierSubmit,
  handleMicrosoftLogin,
  handlePasswordSubmit,
  handleProductSelection,
  handleTermsSubmit,
  handleTwoFactorComplete,
  hasProductAccess,
  identifier,
  identifierForm,
  isIdentifierSubmitting,
  isPasswordSubmitting,
  isSocialPending,
  isTermsSubmitting,
  logOutUser,
  lowerPermissions,
  passwordForm,
  pendingProductSelection,
  productSelectionOptions,
  rememberSignIn,
  resetPasswordHref,
  resolvedProduct,
  setRememberSignInPersisted,
  shouldShowCurrentFlowLoading,
  socialProviderPending,
  ssoDiscovery,
  termsForm,
  userHasTwoFactor,
  view,
}: SignInStepContentProps) => {
  const handleUseSso = () => {
    if (ssoDiscovery?.login_url) {
      window.location.assign(ssoDiscovery.login_url);
    }
  };

  if (shouldShowCurrentFlowLoading) {
    return <LoadingState label={currentLoadingLabel} />;
  }

  if (
    shouldShowSignInSessionStepLoading({
      hasCurrentUser: Boolean(currentUser),
      hasProductAccess,
      view,
    })
  ) {
    return <LoadingState label={currentLoadingLabel} />;
  }

  switch (view) {
    case SIGN_IN_VIEW.IDENTIFIER:
      return (
        <SignInIdentifierStep
          form={identifierForm}
          isSubmitting={isIdentifierSubmitting}
          isSocialPending={isSocialPending}
          onMicrosoftLogin={handleMicrosoftLogin}
          onRememberSignInChange={setRememberSignInPersisted}
          onStartGoogleLogin={handleGoogleLogin}
          onSubmit={handleIdentifierSubmit}
          rememberSignIn={rememberSignIn}
          resetPasswordHref={resetPasswordHref}
          socialProviderPending={socialProviderPending}
        />
      );
    case SIGN_IN_VIEW.PASSWORD:
      return identifier ? (
        <SignInPasswordStep
          form={passwordForm}
          isSubmitting={isPasswordSubmitting}
          onRememberSignInChange={setRememberSignInPersisted}
          onSubmit={handlePasswordSubmit}
          onUseSso={handleUseSso}
          rememberSignIn={rememberSignIn}
          resetPasswordHref={resetPasswordHref}
          showSsoFallback={
            ssoDiscovery?.post_discovery_flow === TenantEmailDiscoveryResponse.post_discovery_flow.SAML_OR_PASSWORD
            && Boolean(ssoDiscovery?.login_url)
          }
        />
      ) : null;
    case SIGN_IN_VIEW.TWO_FACTOR:
      return currentUser ? (
        <OTP
          showActionButtons
          showAuthHeader
          showMandatoryHint={!isEnterpriseSsoSignInSession()}
          userHas2FA={userHasTwoFactor}
          verifyOtp={handleTwoFactorComplete}
        />
      ) : null;
    case SIGN_IN_VIEW.TERMS:
      return currentUser ? (
        <SignInTermsStep
          form={termsForm}
          isSubmitting={isTermsSubmitting}
          onSignOut={logOutUser}
          onSubmit={handleTermsSubmit}
        />
      ) : null;
    case SIGN_IN_VIEW.PRODUCT_SELECTION:
      return currentUser && hasProductAccess ? (
        <SignInProductSelectionStep
          loadingValue={pendingProductSelection}
          onSelect={handleProductSelection}
          options={productSelectionOptions}
        />
      ) : null;
    case SIGN_IN_VIEW.WORKSPACE_SELECTION:
      return currentUser ? (
        <WorkspaceSelector
          isCompanyProduct={resolvedProduct === SIGN_IN_PRODUCT.COMPANY}
          lowerPermissions={lowerPermissions}
        />
      ) : null;
    default:
      return null;
  }
};

export default SignInStepContent;
